import React, { useEffect, useState } from "react";
import DataPoints from "../../api/models/statistics/DataPoints";
import StatisticsApi from "../../api/services/StatisticsApi";
import CustomTable from "../../components/CustomTable";
import LinearIndeterminate from "../../components/Loading/LinearIndeterminate";
import useEnv from "../../hooks/env";
import DateRange from "./DateRange.model";

const COLUMNS_DISPLAY_NAME_MAPPING = {
  completedTestPlans: "Test Plans",
  completedTestSeries: "Test Series",
  completedTestCases: "Test Cases",
  completedTestCaseRepetitions: "Repetitions",
  testedProducts: "Tested Products",
  completedTestCaseDataPoints: "Data Points",
} as const;

type ValsOf<T extends Record<string | number | symbol, unknown>> = T[keyof T];
type RowData = Record<ValsOf<typeof COLUMNS_DISPLAY_NAME_MAPPING>, number>;
type DataPointsKeys = keyof typeof COLUMNS_DISPLAY_NAME_MAPPING;

interface DateRangeTableProps {
  dateRange: DateRange<string> | null;
}

const DataPointsTable: React.FC<DateRangeTableProps> = ({ dateRange }) => {
  const { api } = useEnv();
  const statisticsApi = new StatisticsApi(api);
  const [isLoading, setIsLoading] = useState(false);
  const [rowData, setRowData] = useState<RowData[] | null>(null);

  const createRowData = (dataPoints: DataPoints): RowData => {
    const keys = Object.keys(dataPoints) as DataPointsKeys[];
    return keys.reduce<RowData>((rows, currDataPointName) => {
      const displayName = COLUMNS_DISPLAY_NAME_MAPPING[currDataPointName];
      rows[displayName] = dataPoints[currDataPointName];
      return rows;
    }, {} as RowData);
  };

  useEffect(() => {
    setIsLoading(true);
    statisticsApi
      .fetchDataPoints(dateRange)
      .then(({ data }) => {
        setRowData([createRowData(data)]);
      })
      .finally(() => setIsLoading(false));
  }, [dateRange]);

  return rowData ? (
    <>
      <CustomTable headDense rowOrderFixed columns={Object.values(COLUMNS_DISPLAY_NAME_MAPPING)} rows={rowData} />
    </>
  ) : (
    <LinearIndeterminate loading={isLoading || false} />
  );
};

export default DataPointsTable;
